import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormGroup, Validators } from '@angular/forms';

import { Subject, Subscription, debounceTime, distinctUntilChanged, map } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { INPUT_DEBOUNCE_TIME, capitalize, trimAndReplaceWhitespace, unsubscribe } from '@fcom/core';

import { IconButtonTheme, IconButtonSize } from '../../../components/buttons';
import { CursorType, InputType, TextInputType } from '../enums';
import { PopoverOptions } from '../../popover';
import { AriaOptions } from '../../interfaces';
import { IconSet } from '../../../components/icons';

@Component({
  selector: 'fcom-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['text-input.component.scss'],
  standalone: false,
})
export class TextInputComponent implements OnInit, OnDestroy {
  @Input()
  parentForm: UntypedFormGroup;

  /**
   * Name of this control.
   * Will also be used in this components `data-testid` and `data-testid`s that are passed to child `<fcom-icon-button>`s
   */
  @Input()
  controlName: string;

  @Input()
  helpText?: string;

  @Input()
  translationPrefix: string;

  @Input()
  placeHolder: string = null;

  @Input()
  placeHolderText?: string;

  @Input()
  characterCount: number;

  @Input()
  characterLimit: number;

  @Input()
  isSensitive = false;

  @Input()
  isResizeable = false;

  @Input()
  type: TextInputType = TextInputType.INPUT;

  @Input()
  rows = 6;

  @Input()
  id: string;

  @Input()
  labelPrefix: string;

  @Input()
  labelSuffix: string;

  @Input()
  label: string;

  @Input()
  displayMargin = true;

  @Input()
  visualDisable = false;

  @Input()
  required: boolean;

  @Input()
  translationErrorPrefix: string;

  @Input() hideErrorLabel = false;

  @Input()
  hideLabel: boolean;

  @Input()
  inputType: InputType = InputType.TEXT;

  @Input()
  tooltipHeading: string;

  @Input()
  tooltipContent: string;

  @Input()
  useCustomTooltipAction = false;

  @Input()
  tooltipOptions: PopoverOptions;

  @Input()
  icon: IconSet;

  @Input()
  ariaOptions: AriaOptions = {};

  @Input()
  isPassword = false;

  @Input()
  capitalize = false;

  @Input()
  uppercase = false;

  @Input()
  clearInput = false;

  @Input()
  disableAutoComplete?: boolean;

  @Input()
  cursorType?: CursorType;

  @Input()
  trim = true;

  @Output()
  tooltipClicked: EventEmitter<Event> = new EventEmitter();

  @Output()
  inputEvent: EventEmitter<Event> = new EventEmitter();

  @Output()
  iconClicked: EventEmitter<Event> = new EventEmitter();

  @Output()
  blurEvent: EventEmitter<FocusEvent> = new EventEmitter();

  isRequired = false;
  showAsterix = false;
  idOrControlName: string;
  control: AbstractControl;
  isPasswordVisible = false;
  showClearIcon = false;

  @ViewChild('inputElement', { static: false })
  inputElement: ElementRef;

  @ViewChild('textareaElement', { static: false })
  textareaElement: ElementRef;

  readonly CursorType = CursorType;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly InputType = InputType;
  readonly TextInputType = TextInputType;

  private subscriptions: Subscription = new Subscription();
  private inputSubject = new Subject<Event>();

  get fieldRequired(): boolean {
    const hasRequiredValidator = this.ctrlField.hasValidator(Validators.required);

    /* Checking many variables here because depending on how the parent component
     * creates the validators for this field, some of these might be false or null.
     * hasRequiredValidator works correctly when parent uses Validators.required,
     * this.ctrlField.errors.required works partly when parent uses Validators.compose([Validators.required]).
     * Partly meaning that if field is pre-filled and page is refreshed, then the asterisk (*) disappears from
     * the field label. */
    return this.required || this.ctrlField.errors?.required || hasRequiredValidator;
  }

  get ctrlField(): AbstractControl {
    return this.parentForm.get(this.controlName);
  }

  ngOnInit(): void {
    this.control = this.ctrlField;
    this.isRequired = this.fieldRequired;
    this.showAsterix = this.required ?? this.isRequired;
    this.idOrControlName = this.id || this.controlName;
    this.toggleIcon();

    this.subscriptions.add(
      this.control.statusChanges.subscribe(() => {
        if (this.control.value) {
          return;
        }
        this.isRequired = this.fieldRequired;
      })
    );

    if (this.uppercase || this.capitalize) {
      this.subscriptions.add(
        this.inputSubject
          .pipe(
            debounceTime(INPUT_DEBOUNCE_TIME),
            map((event) => event.target as HTMLInputElement),
            distinctUntilChanged(null, (element) => element.value)
          )
          .subscribe((input) => {
            const transform: (value: string) => string = this.uppercase
              ? (value: string) => value.toUpperCase()
              : (value: string) => capitalize(value);
            this.formatValue(input, transform);
          })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  handleIconClick(): void {
    if (this.isPassword) {
      this.togglePasswordVisibility();
    } else {
      this.clearInputField();
    }
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  toggleIcon(): void {
    this.showClearIcon = this.parentForm.get(this.controlName).value;
  }

  clearInputField(): void {
    this.parentForm.get(this.controlName).setValue('');
  }

  private formatValue(input: HTMLInputElement, transform: (value: string) => string): void {
    const { value, selectionStart } = input;
    const newValue = transform(value);

    if (value !== newValue) {
      this.ctrlField.setValue(newValue);
      input.setSelectionRange(selectionStart, selectionStart);
    }
  }

  onInput(event: Event): void {
    if (this.uppercase || this.capitalize) {
      this.inputSubject.next(event);
    }
    this.inputEvent.emit(event);
  }

  onBlur(event: FocusEvent): void {
    if (this.trim) {
      this.control.setValue(trimAndReplaceWhitespace(this.control.getRawValue()));
    }

    this.blurEvent.emit(event);
  }

  onEnter(): void {
    if (this.trim) {
      this.control.setValue(trimAndReplaceWhitespace(this.control.getRawValue()));
    }
  }

  onPaste(): void {
    if (!this.trim) {
      return;
    }

    // Without this the field value does not include the pasted content.
    setTimeout(() => {
      this.control.setValue(trimAndReplaceWhitespace(this.control.getRawValue()));
    }, 0);
  }
}
